import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Settings, CreditCard, Check, Webhook, Copy, ExternalLink, Tag } from 'lucide-react';
import StripeWebhookEventLog from '../../components/admin/StripeWebhookEventLog';
import AdminPricingManager from '../../components/admin/AdminPricingManager';

export default function AdminSettings() {
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold flex items-center gap-2">
          <Settings className="w-7 h-7" />
          Platform Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage Stripe payments, plan pricing & promo codes</p>
      </div>

      <Tabs defaultValue="payments" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="payments" className="flex items-center gap-2" data-testid="admin-settings-payments-tab">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Payments</span>
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2" data-testid="admin-settings-pricing-tab">
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Pricing & Promos</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="space-y-6">
          {/* Stripe Webhook Setup */}
          <Card data-testid="stripe-webhook-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="w-5 h-5 text-[#635bff]" />
                Stripe Webhook
              </CardTitle>
              <CardDescription>
                Optional but recommended. Configure this URL in your Stripe dashboard so payments are confirmed even if the user closes their browser before returning.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Webhook endpoint URL</Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/api/webhook/stripe`}
                    className="font-mono text-sm"
                    data-testid="webhook-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/api/webhook/stripe`);
                      toast.success('Webhook URL copied');
                    }}
                    data-testid="copy-webhook-url-btn"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 p-4 text-sm space-y-2">
                <p className="font-medium">Setup steps</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Open <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-[#635bff] underline inline-flex items-center gap-0.5">Stripe → Developers → Webhooks <ExternalLink className="w-3 h-3" /></a> and click <strong>Add endpoint</strong>.</li>
                  <li>Paste the endpoint URL above.</li>
                  <li>Under <em>Events to send</em>, select <code className="bg-background px-1 rounded">checkout.session.completed</code> (and optionally <code className="bg-background px-1 rounded">checkout.session.async_payment_succeeded</code>).</li>
                  <li>Save. Stripe shows a <strong>Signing secret</strong> (starts with <code className="bg-background px-1 rounded">whsec_...</code>) — copy it.</li>
                  <li>Add it to your backend env: <code className="bg-background px-1 rounded">STRIPE_WEBHOOK_SECRET=whsec_...</code> and restart the backend.</li>
                </ol>
              </div>

              <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm flex gap-2">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800 dark:text-emerald-200">Your endpoint is live</p>
                  <p className="text-emerald-700 dark:text-emerald-300 text-xs mt-0.5">
                    The webhook handler is already wired and verifies signatures via Stripe's library. Until you complete the dashboard setup, the existing polling fallback (<code>/checkout-status/&#123;id&#125;</code>) keeps payment confirmation working — so this step is just a resilience upgrade.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <StripeWebhookEventLog />
        </TabsContent>

        <TabsContent value="pricing">
          <AdminPricingManager />
        </TabsContent>
      </Tabs>

    </div>
  );
}
